import { Component, inject, signal } from '@angular/core';

import { Booking, DashboardStats, Locker, User } from '../../models/domain.models';
import { ApiService } from '../../services/api.service';
import { mockStats } from '../../services/mock-data';
import { BookingTableComponent } from './components/booking-table.component';
import { DigitalTwinComponent } from './components/digital-twin.component';
import { LockerStatusCardComponent } from './components/locker-status-card.component';
import { MapPreviewComponent } from './components/map-preview.component';
import { StatsCardComponent } from './components/stats-card.component';

@Component({
  selector: 'vx-dashboard',
  standalone: true,
  imports: [StatsCardComponent, LockerStatusCardComponent, BookingTableComponent, MapPreviewComponent, DigitalTwinComponent],
  template: `
    <section class="dash">
      <aside>
        <strong>VaulTriX Cloud</strong>
        <a class="active">Admin Overview</a>
        <a>Partner Hub</a>
        <a>Operator Console</a>
        <a>User Area</a>
        <a>IoT Devices</a>
      </aside>

      <div class="content">
        <header class="dash-header">
          <div>
            <span class="eyebrow">Dashboard live demo</span>
            <h1>{{ roleTitle() }}</h1>
            <p>{{ user()?.nome || 'Admin VaulTriX' }} / Smart locker network live</p>
          </div>
          <button class="btn primary" type="button">Aggiungi locker</button>
        </header>

        <div class="stats-grid">
          @for (card of stats().cards; track card.label) {
            <vx-stats-card [label]="card.label" [value]="card.value" [trend]="card.trend" />
          }
        </div>

        <section class="panel-grid">
          <div class="panel-card">
            <div class="panel-heading"><h2>Mappa locker</h2><span>Live</span></div>
            <vx-map-preview [lockers]="lockers()" />
          </div>
          <vx-digital-twin />
        </section>

        <section class="panel-grid wide">
          <div class="panel-card">
            <div class="panel-heading"><h2>AI Smart Insights</h2><span>Demo data</span></div>
            <div class="insights">
              @for (insight of stats().ai_insights; track insight) {
                <article>{{ insight }}</article>
              }
            </div>
          </div>
          <div class="panel-card">
            <div class="panel-heading"><h2>Utilizzo giornaliero</h2><span>7 giorni</span></div>
            <div class="chart">
              @for (point of stats().daily_usage; track point.day) {
                <div><i [style.height.%]="point.uses"></i><small>{{ point.day }}</small></div>
              }
            </div>
          </div>
        </section>

        <section class="panel-grid wide">
          <div class="panel-card">
            <div class="panel-heading"><h2>Stato dispositivi</h2><span>Online / offline</span></div>
            <div class="locker-list">
              @for (locker of lockers(); track locker.id) {
                <vx-locker-status-card [nome]="locker.nome" [citta]="locker.citta" [posizione]="locker.posizione" [stato]="locker.stato" />
              }
            </div>
          </div>
          <div class="panel-card">
            <div class="panel-heading"><h2>Prenotazioni e accessi</h2><span>Ultimi eventi</span></div>
            <vx-booking-table [bookings]="bookings()" />
          </div>
        </section>
      </div>
    </section>
  `,
  styles: [`
    .dash {
      min-height: 100vh;
      display: grid;
      grid-template-columns: 250px 1fr;
      background: #edf2ff;
    }
    aside {
      position: sticky;
      top: 0;
      height: 100vh;
      padding: 28px 18px;
      color: white;
      background: linear-gradient(180deg, #080c1d, #121a39);
      display: grid;
      align-content: start;
      gap: 8px;
    }
    aside strong {
      font-family: "Space Grotesk";
      font-size: 1.35rem;
      margin-bottom: 18px;
    }
    aside a {
      padding: 12px;
      border-radius: 12px;
      color: rgba(255,255,255,.68);
      font-weight: 800;
    }
    aside a.active, aside a:hover {
      color: white;
      background: rgba(255,255,255,.1);
    }
    .content {
      padding: 34px;
      display: grid;
      gap: 22px;
    }
    .dash-header {
      display: flex;
      justify-content: space-between;
      gap: 18px;
      align-items: start;
    }
    h1 {
      margin: 6px 0;
      font-family: "Space Grotesk";
      font-size: clamp(2rem, 4vw, 3.4rem);
    }
    .dash-header p { margin: 0; color: var(--vx-muted); font-weight: 700; }
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 14px;
      padding: 18px;
      border-radius: 20px;
      background: linear-gradient(135deg, var(--vx-dark), var(--vx-dark-2));
    }
    .panel-grid {
      display: grid;
      grid-template-columns: 1.25fr .75fr;
      gap: 18px;
    }
    .panel-grid.wide {
      grid-template-columns: 1fr 1fr;
    }
    .insights {
      display: grid;
      gap: 10px;
    }
    .insights article {
      padding: 14px;
      border-radius: 8px;
      background: linear-gradient(135deg, rgba(109,93,252,.12), rgba(0,163,255,.08));
      border: 1px solid rgba(109,93,252,.16);
      font-weight: 800;
    }
    .chart {
      height: 230px;
      display: flex;
      gap: 10px;
      align-items: end;
    }
    .chart div {
      flex: 1;
      height: 100%;
      display: grid;
      align-items: end;
      gap: 8px;
      text-align: center;
      color: var(--vx-muted);
      font-size: .76rem;
      font-weight: 800;
    }
    .chart i {
      display: block;
      min-height: 22px;
      border-radius: 8px 8px 0 0;
      background: linear-gradient(180deg, var(--vx-accent), var(--vx-primary));
      animation: reveal 700ms ease both;
    }
    .locker-list {
      display: grid;
      gap: 10px;
    }
    @media (max-width: 1100px) {
      .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .panel-grid, .panel-grid.wide { grid-template-columns: 1fr; }
    }
    @media (max-width: 760px) {
      .dash { grid-template-columns: 1fr; }
      aside {
        position: static;
        height: auto;
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      aside strong { grid-column: 1 / -1; }
      .content { padding: 22px 16px; }
      .dash-header { display: grid; }
      .stats-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class DashboardComponent {
  private readonly api = inject(ApiService);
  protected readonly stats = signal<DashboardStats>(mockStats);
  protected readonly lockers = signal<Locker[]>([]);
  protected readonly bookings = signal<Booking[]>([]);
  protected readonly user = signal<User | null>(this.readUser());

  constructor() {
    this.api.getAdminStats().subscribe((stats) => this.stats.set(stats));
    this.api.getLockers().subscribe((lockers) => this.lockers.set(lockers));
    this.api.getBookings().subscribe((bookings) => this.bookings.set(bookings));
  }

  protected roleTitle() {
    const role = this.user()?.ruolo || 'admin';
    if (role === 'partner') return 'Dashboard Partner';
    if (role === 'user') return 'Dashboard Utente';
    if (role === 'operator') return 'Console Operatore';
    return 'Dashboard Admin VaulTriX';
  }

  private readUser(): User | null {
    const raw = localStorage.getItem('vx_user');
    return raw ? JSON.parse(raw) as User : null;
  }
}
